/**
 * A client's own condition deadlines (Lloyd, 6 Oct 2026). Mirrors ClientDeadline in
 * backend/src/utils/clientPortal/clientDeadlines.ts.
 */
export type ClientDeadlineState = 'overdue' | 'due-soon' | 'upcoming' | 'no-date' | 'done';

export interface ClientDeadline {
  guid: string;
  ticketGuid: string;
  sentence: string;
  /** YYYY-MM-DD, or null when not set yet. */
  dueAt: string | null;
  completedAt: string | null;
  daysLeft: number | null;
  state: ClientDeadlineState;
}

export default class ClientDeadlinesApi {
  /** All the client's deadlines, or one case's when a guid is given. */
  static async list(ticketGuid?: string): Promise<ClientDeadline[]> {
    const query = ticketGuid ? `?Ticket_guid=${encodeURIComponent(ticketGuid)}` : '';
    const response = await fetch(`${process.env.API_HOST || ''}/api/v1/tickets/deadlines${query}`, {
      credentials: 'include',
    });
    if (!response.ok) throw new Error('Could not load your deadlines');
    return response.json();
  }
}

/** "Fri, Oct 9, 2026" from a YYYY-MM-DD, read as that calendar day wherever the browser is. */
export function formatDeadlineDate(day: string): string {
  const [y, m, d] = day.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/** "Due today", "3 days left", "2 days overdue". */
export function describeDaysLeft(deadline: ClientDeadline): string | null {
  const n = deadline.daysLeft;
  if (n === null) return null;
  if (n === 0) return 'Due today';
  if (n === 1) return 'Due tomorrow';
  if (n > 1) return `${n} days left`;
  return n === -1 ? '1 day overdue' : `${-n} days overdue`;
}
